import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { collection, query, where, orderBy, onSnapshot, updateDoc, deleteDoc, deleteField, doc, serverTimestamp, getDocs, limit, runTransaction } from 'firebase/firestore';
import ChargeOrderModal from '../ChargeOrderModal';
import ChangeTableModal from './ChangeTableModal';
import { processOrderSale } from '../../lib/saleService';
import { db } from '../../firebase';
import IceCreamPOS from '../POS/IceCreamPOS';
import RecipeChoicePOS from '../POS/RecipeChoicePOS';
import { useSettings } from '../../context/SettingsContext';
import { printKitchen, printTicket } from '../../services/printService';
import { normalizePrintSettings } from '../../lib/printing';
import { requiresDirectStock } from '../../lib/productStockPolicy';
import { isProductAvailableToday, resolvePromotionProduct } from '../../lib/promotionRules';
import {
  DISPATCH_STAGE,
  canDeleteDispatchOrder,
  canManageDispatchOrder,
  canChargeDispatchOrder,
  canPrintDispatchOrder,
  canEditDispatchContents,
  canUseSharedDispatchCash,
  canChangeOrderTable,
  dispatchStagePriority,
  getDispatchStage,
  isPaymentPending,
} from '../../lib/dispatchOrders';


// --- Impresión sin ventanas: iframe oculto reutilizable ---
let __printFrame = typeof window !== 'undefined' ? window.__printFrame || null : null;

function getPrintFrame() {
  if (typeof document === 'undefined') return null;
  if (__printFrame && document.body.contains(__printFrame)) return __printFrame;
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);
  __printFrame = iframe;
  if (typeof window !== 'undefined') window.__printFrame = iframe;
  return iframe;
}

function printWithIframe(htmlString) {
  const iframe = getPrintFrame();
  if (!iframe) return;
  const win = iframe.contentWindow;
  const doc = win.document;

  doc.open();
  doc.write(`
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Ticket</title>
        <style>
          body { margin:0; padding:12px; font:14px monospace; white-space:pre-wrap; }
          .ticket { white-space: pre-wrap; }
          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
          @page { size: auto; margin: 0; }
        </style>
      </head>
      <body>${htmlString}</body>
    </html>
  `);
  doc.close();

  const cleanup = () => { win.removeEventListener('afterprint', cleanup); };
  win.addEventListener('afterprint', cleanup);

  setTimeout(() => { win.focus(); win.print(); }, 0);
  setTimeout(cleanup, 3000); // respaldo
}

const formatOrderId = (id) => {
  if (!id) return '';
  if (typeof id !== 'string') return String(id);
  if (id.length <= 8) return id;
  return `${id.slice(0, 6)}…`;
};


// --- Helpers de disponibilidad para anexar desde cocina ---
const norm = (s='') => s.normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().trim();

// Prioriza username (porque POS guarda 'user' con username)
// luego uid, luego email (Turnos Admin guardó 'email').
const getCashierKey = (u) => u?.username || u?.uid || u?.email || String(u || '');

// Busca turno del usuario con fallback si falta índice compuesto
async function getOpenTurnForUser(user) {
  const keys = [user?.username, user?.uid, user?.email].filter(Boolean);

  for (const key of keys) {
    // 1) Query óptima (requiere índice: user + estado + openedAt)
    try {
      const q1 = query(
        collection(db, 'cajas'),
        where('user', '==', key),
        where('estado', '==', 'abierta'),
        orderBy('openedAt', 'desc'),
        limit(1)
      );
      const s1 = await getDocs(q1);
      if (!s1.empty) {
        const d = s1.docs[0];
        return { id: d.id, ...d.data() };
      }
    } catch (e) {
      console.warn('getOpenTurnForUser: falta índice (user, estado, openedAt). Fallback…', e);
    }

    // 2) Fallback SIN orderBy (evita índice compuesto)
    try {
      const q2 = query(
        collection(db, 'cajas'),
        where('user', '==', key),
        where('estado', '==', 'abierta'),
        limit(1)
      );
      const s2 = await getDocs(q2);
      if (!s2.empty) {
        const d = s2.docs[0];
        return { id: d.id, ...d.data() };
      }
    } catch (e2) {
      console.error('getOpenTurnForUser (fallback) falló:', e2);
    }
  }

  return null;
}

async function ensureTurnForUser(user) {
  const t = await getOpenTurnForUser(user);
  if (!t) throw new Error('No tienes base asignada. Solicítala al administrador.');
  return t;
}

async function ensureTurnGeneral(currentUser) {
  const mine = await getOpenTurnForUser(currentUser);
  if (mine) return mine;
  const any = await getAnyOpenTurn();
  if (any) return any;
  throw new Error('No hay ninguna caja abierta.');
}


function indexById(list = []) {
  const m = new Map();
  list.forEach(p => m.set(p.id, p));
  return m;
}

/**
 * Máximo número de unidades producibles para un producto tipo receta
 * según sus baseItems (id, qty, unit).
 */
function maxUnitsForRecipe(product, allById) {
  const base = product?.config?.recipe?.baseItems;
  if (!Array.isArray(base) || base.length === 0) return 0;

  let max = Infinity;
  for (const bi of base) {
    const insumo = allById.get(bi.productId || bi.id);
    if (!insumo) return 0;
    const stock = Number(insumo.stock || 0);
    const req = Number(bi.qty || bi.quantity || 0);
    if (req <= 0) continue;        // evita división por 0
    max = Math.min(max, Math.floor(stock / req));
  }
  return Number.isFinite(max) ? max : 0;
}

/**
 * Máximo producible para helados:
 * - 1 base (cono/vaso) por unidad (usa baseInsumoId)
 * - servilletas/cucharas si están configuradas
 * - gramos de sabores: suma el stock (en g) de TODOS los insumos de helado
 *   y divide por (sabores * gramosPorSabor)
 */
function maxUnitsForIce(product, allProducts) {
  const cfg = product?.config || {};
  const sabores = Number(cfg.sabores || 1);
  const gramosPorSabor = Number(cfg.gramosPorSabor || 0);
  const serv = Number(cfg.servilletas || 0);
  const cuch = Number(cfg.cucharas || 0);
  const extraItems = Array.isArray(cfg.extraItems) ? cfg.extraItems : [];

  let max = Infinity;

  // base (vaso/cono) por unidad
  if (cfg.baseInsumoId) {
    const baseInsumo = allProducts.find(p => p.id === cfg.baseInsumoId);
    const baseStock = Number(baseInsumo?.stock || 0);
    max = Math.min(max, baseStock); // 1 por unidad
  }

  // servilletas
  if (serv > 0) {
    const servInsumo = allProducts.find(p => norm(p.name).includes('servillet') && String(p.unit).toLowerCase() === 'unidad');
    const sStock = Number(servInsumo?.stock || 0);
    if (serv > 0) max = Math.min(max, Math.floor(sStock / serv));
  }

  // cucharas
  if (cuch > 0) {
    const cuchInsumo = allProducts.find(p => norm(p.name).includes('cuchar') && String(p.unit).toLowerCase() === 'unidad');
    const cStock = Number(cuchInsumo?.stock || 0);
    if (cuch > 0) max = Math.min(max, Math.floor(cStock / cuch));
  }

  // gramos totales de “helado” disponibles (unit === 'g')
  extraItems.forEach((item) => {
    const insumo = allProducts.find((p) => p.id === item.productId);
    const qty = Number(item.qty || 0);
    if (!insumo || qty <= 0) return;
    const stock = Number(insumo.stock || 0);
    max = Math.min(max, Math.floor(stock / qty));
  });

  if (sabores > 0 && gramosPorSabor > 0) {
    const gramosNecesarios = sabores * gramosPorSabor;
    // suma todos los insumos que sean en gramos y se identifiquen como helado
    const totalGDisponibles = allProducts
      .filter(p => String(p.unit).toLowerCase() === 'g')
      .filter(p => norm(p.name).startsWith('helado') || norm(p.category).includes('helado'))
      .reduce((acc, p) => acc + Number(p.stock || 0), 0);

    if (gramosNecesarios > 0) {
      const maxPorGramos = Math.floor(totalGDisponibles / gramosNecesarios);
      max = Math.min(max, maxPorGramos);
    }
  }

  return Number.isFinite(max) ? max : 0;
}

const safeList = (arr) => (Array.isArray(arr) ? arr : []);

const makeLineId = () =>
  (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `line-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const getRecipeComponentsFromProduct = (product, catalogProducts = []) => {
  if (!product) return [];

  const baseItems =
    safeList(product?.config?.recipe?.baseItems).length > 0
      ? product.config.recipe.baseItems
      : safeList(product?.config?.baseItems);

  const componentsFromConfig = safeList(baseItems).map((bi) => {
    const compId = bi.productId || bi.id;
    if (!compId) return null;
    const catalogComponent = catalogProducts.find((item) => item.id === compId);
    if (!catalogComponent || catalogComponent.deleted === true) return null;
    return {
      componentId: compId,
      insumoId: compId,
      qtyPerUnit: Number(bi.qty || bi.qtyPerUnit || bi.quantity || 1),
      name: catalogComponent.name || bi.name || bi.productName || '',
      unit: catalogComponent.unit || bi.unit || 'unidad',
      costPrice: Number(catalogComponent.costPrice || 0),
    };
  }).filter(Boolean);

  if (componentsFromConfig.length > 0) return componentsFromConfig;

  return safeList(product?.recipeComponents)
    .map((comp) => {
      const compId = comp.insumoId || comp.productId || comp.id;
      if (!compId) return null;
      return {
        componentId: compId,
        insumoId: compId,
        qtyPerUnit: Number(comp.qtyPerUnit || comp.qty || comp.quantity || 1),
        name: comp.name || comp.productName || '',
        unit: comp.unit || 'unidad',
      };
    })
    .filter(Boolean);
};


const KitchenView = ({ onBack, onOrderReady, currentUser, products = [], productsReady = false }) => {
  const { settings } = useSettings();
  const printingSettings = useMemo(() => normalizePrintSettings(settings?.printing), [settings?.printing]);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [busy, setBusy] = useState({}); // { [orderId]: true }
const [charging, setCharging] = useState({}); // { [orderId]: true }

// Cobro desde despacho
const [showCharge, setShowCharge] = useState(false);
const [orderToCharge, setOrderToCharge] = useState(null);
const [checkingTurn, setCheckingTurn] = useState(true);
const [hasOpenTurn, setHasOpenTurn] = useState(false);
// Anexar producto
const [addModal, setAddModal] = useState({ open:false, order:null });
const [productSearch, setProductSearch] = useState('');
const [catalog, setCatalog] = useState([]);   // productos vendibles
const [selectedProd, setSelectedProd] = useState(null);
const [qty, setQty] = useState(1);
 const [allProducts, setAllProducts] = useState([]);   // ← insumos + finales (para IceCreamPOS)
 const [iceModal, setIceModal] = useState({ open:false, product:null });
 const [recipeChoiceModal, setRecipeChoiceModal] = useState({ open:false, product:null });
const [removingItems, setRemovingItems] = useState({});
const [deletingOrders, setDeletingOrders] = useState({});
const [deleteDialog, setDeleteDialog] = useState(null);
const [tableSearch, setTableSearch] = useState('');
const [changingTableOrder, setChangingTableOrder] = useState(null);
const [statusFilter, setStatusFilter] = useState('Todos');
const [snapshotError, setSnapshotError] = useState('');
const [soundEnabled, setSoundEnabled] = useState(false);
const [clockTick, setClockTick] = useState(() => Date.now());
const audioContextRef = useRef(null);
const skipNextAlarmRef = useRef(false);

// Pedidos crudos desde Firestore (antes de filtrar por caja)
const [rawOrders, setRawOrders] = useState([]);

const getAudioContext = useCallback(() => {
  if (typeof window === 'undefined') return null;
  if (!audioContextRef.current) {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    audioContextRef.current = new AudioContextClass();
  }
  return audioContextRef.current;
}, []);

const playDispatchAlert = useCallback(async () => {
  const context = getAudioContext();
  if (!context) throw new Error('Este navegador no permite reproducir la alerta.');
  if (context.state === 'suspended') await context.resume();

  const startAt = context.currentTime + 0.03;
  const notes = [880, 1175, 880, 1320];
  notes.forEach((frequency, index) => {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const noteStart = startAt + (index * 0.28);
    const noteEnd = noteStart + 0.2;
    oscillator.type = 'square';
    oscillator.frequency.setValueAtTime(frequency, noteStart);
    gain.gain.setValueAtTime(0.0001, noteStart);
    gain.gain.exponentialRampToValueAtTime(0.32, noteStart + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.0001, noteEnd);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(noteStart);
    oscillator.stop(noteEnd + 0.02);
  });
}, [getAudioContext]);

const enableSound = async () => {
  try {
    await playDispatchAlert();
    skipNextAlarmRef.current = true;
    setSoundEnabled(true);
  } catch (error) {
    console.error('No se pudo activar la alerta de despacho:', error);
    alert(error?.message || 'No se pudo activar el sonido. Revisa el volumen del equipo.');
  }
};

const disableSound = () => setSoundEnabled(false);


const openAddProduct = (order) => {
  const all = Array.isArray(products) ? products : [];
  if (!productsReady || all.length === 0) {
    alert('El catálogo todavía se está cargando. Intenta nuevamente en unos segundos.');
    return;
  }
  setAddModal({ open:true, order });
  setSelectedProd(null); setProductSearch(''); setQty(1);
 setAllProducts(all); // 👈 para IceCreamPOS

  // Vendibles = no-insumo
  const sellables = all
    .filter(p => p?.deleted !== true && String(p?.type || '').toLowerCase() !== 'insumo')
    .filter(p => isProductAvailableToday(p))
    .map(p => resolvePromotionProduct(p, all))
    .filter(Boolean);

  // Índice por id para recetas
  const mapAll = indexById(all);

  // Enriquecer cada producto con _maxUnits (cuántas unidades puedo producir hoy)
  const withCap = sellables.map(p => {
    const isRecipe = !!p?.config?.recipe;
    const isIce    = !!p?.config?.isIceCream;
    const allowNegativeStock = isRecipe || isIce || !requiresDirectStock(p);

    if (isRecipe) {
      return { ...p, _maxUnits: maxUnitsForRecipe(p, mapAll), _allowNegativeStock: allowNegativeStock };
    }
    if (isIce) {
      return { ...p, _maxUnits: maxUnitsForIce(p, all), _allowNegativeStock: allowNegativeStock };
    }
    // Producto “normal”: usar stock propio
    return {
      ...p,
      _maxUnits: allowNegativeStock ? 0 : Math.max(0, Number(p.stock || 0)),
      _allowNegativeStock: allowNegativeStock,
    };
  });

  // Las recetas siguen disponibles; los productos normales requieren stock.
  setCatalog(withCap.filter(p => p._allowNegativeStock || Number(p._maxUnits || 0) > 0));
};

const handleAcknowledgeOrder = async (order) => {
  if (busy[order.id]) return;
  if (!canManageDispatchOrder(currentUser, order)) {
    alert('Tu usuario no puede gestionar este pedido.');
    return;
  }
  try {
    setBusy(b => ({ ...b, [order.id]: true }));
    await updateDoc(doc(db, 'orders', order.id), {
      dispatchStatus: 'En Preparacion',
      acknowledgedAt: serverTimestamp(),
      acknowledgedByUid: currentUser?.uid || null,
      acknowledgedByName: currentUser?.username || currentUser?.email || 'Despacho',
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error(e);
    alert('No se pudo confirmar el pedido. Intenta nuevamente.');
  } finally {
    setBusy(b => ({ ...b, [order.id]: false }));
  }
};

const handleMarkDelivered = async (order) => {
  if (busy[order.id]) return;
  if (!canManageDispatchOrder(currentUser, order)) {
    alert('Tu usuario no puede gestionar este pedido.');
    return;
  }
  if (!window.confirm('¿Confirmas que el pedido ya fue entregado? Esta acción NO lo marcará como cobrado.')) return;
  try {
    setBusy(b => ({ ...b, [order.id]: true }));
    await updateDoc(doc(db, 'orders', order.id), {
      dispatchStatus: 'Entregado',
      status: 'Listo para Cobro',
      deliveredAt: serverTimestamp(),
      deliveredByUid: currentUser?.uid || null,
      deliveredByName: currentUser?.username || currentUser?.email || 'Despacho',
      readyForPaymentAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    if (typeof onOrderReady === 'function') onOrderReady(order.id);
  } catch (e) {
    console.error(e);
    alert('No se pudo marcar como entregado. Intenta nuevamente.');
  } finally {
    setBusy(b => ({ ...b, [order.id]: false }));
  }
};

const handleRestoreToPreparing = async (order) => {
  if (busy[order.id]) return;
  if (!canManageDispatchOrder(currentUser, order)) {
    alert('Tu usuario no puede gestionar este pedido.');
    return;
  }
  if (!window.confirm('¿Regresar este pedido de Entregado a En preparación? El pedido seguirá pendiente de cobro.')) return;
  try {
    setBusy(b => ({ ...b, [order.id]: true }));
    await updateDoc(doc(db, 'orders', order.id), {
      dispatchStatus: 'En Preparacion',
      status: 'En Preparacion',
      readyForPaymentAt: deleteField(),
      deliveryRevertedAt: serverTimestamp(),
      deliveryRevertedByUid: currentUser?.uid || null,
      deliveryRevertedByName: currentUser?.username || currentUser?.email || 'Despacho',
      updatedAt: serverTimestamp(),
    });
  } catch (e) {
    console.error(e);
    alert('No se pudo regresar el pedido a preparación. Intenta nuevamente.');
  } finally {
    setBusy(b => ({ ...b, [order.id]: false }));
  }
};


const confirmAddProduct = async () => {
  try {
    const order = addModal.order;
    if (!order || !selectedProd) return;

    const q = Math.max(1, Number.parseInt(qty, 10) || 1);

    const chosen = catalog.find(p => p.id === selectedProd.id) || selectedProd;
    const cap = Number(chosen._maxUnits || 0);
    const allowNegativeStock = chosen._allowNegativeStock === true;

    if (!allowNegativeStock && cap <= 0) {
      throw new Error('Este producto no se puede producir con los insumos actuales.');
    }
    if (!allowNegativeStock && q > cap) {
      throw new Error(`Solo puedes anexar hasta ${cap} unidad(es) con el stock actual de insumos.`);
    }

    const recipeComponents = getRecipeComponentsFromProduct(selectedProd, allProducts);
    if (selectedProd?.config?.recipe && recipeComponents.length === 0) {
      throw new Error(`No se puede anexar "${selectedProd.name}": la receta no tiene insumos válidos configurados.`);
    }

    await runTransaction(db, async (tx) => {
      const ref = doc(db, 'orders', order.id);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Pedido no existe');
      const data = snap.data();
      const items = Array.isArray(data.items) ? [...data.items] : [];
      const idx = items.findIndex(it => it.id === selectedProd.id);
      if (idx >= 0) {
        const existing = items[idx];
        const componentsToUse =
          existing.recipeComponents?.length
            ? existing.recipeComponents
            : recipeComponents.length
              ? recipeComponents
              : null;
        const updated = {
          ...existing,
          quantity: Number(existing.quantity || 0) + q,
          _lineId: existing._lineId || makeLineId(),
        };
        if (componentsToUse?.length) {
          updated.recipeComponents = componentsToUse;
        } else {
          delete updated.recipeComponents;
        }
        items[idx] = updated;
      } else {
        const newItem = {
          id: selectedProd.id,
          name: selectedProd.name,
          price: Number(selectedProd.price || 0),
          quantity: q,
          barcode: selectedProd.barcode || '',
          _lineId: makeLineId(),
        };
        if (recipeComponents.length) {
          newItem.recipeComponents = recipeComponents;
        }
        items.push(newItem);
      }
      const { subtotal, taxes, total } = calcTotals(items);
      tx.update(ref, { items, subtotal, taxes, total, updatedAt: serverTimestamp() });
    });
    setAddModal({ open: false, order: null });
    setSelectedProd(null); setQty(1);
  } catch (e) {
    console.error(e);
    alert(e?.message || 'No se pudo anexar el producto.');
  }
};
// Totales
const calcTotals = (items=[]) => {
 const subtotal = items.reduce((s,i)=> s + Number(i.price||0)*Number(i.quantity||0), 0);
 const taxRate = settings?.taxEnabled ? Number(settings?.taxRate || 0) : 0;
 const taxes = Math.round(subtotal * (taxRate / 100));
 return { subtotal, taxes, total: subtotal + taxes };
};

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  });

const removeItemFromOrder = async (order, item, lineKey) => {
  if (!order || !item) return;
  const key = lineKey || `${order.id}-${item.id}`;
  setRemovingItems(prev => ({ ...prev, [key]: true }));
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db,'orders', order.id);
      const snap = await tx.get(ref);
      if (!snap.exists()) throw new Error('Pedido no encontrado.');
      const data = snap.data();
      const items = Array.isArray(data.items) ? [...data.items] : [];
      const targetKey = lineKey || item._lineId || `${order.id}-${item.id}`;
      const targetIndex = items.findIndex((it) => {
        const existingKey = it._lineId || `${order.id}-${it.id}`;
        return existingKey === targetKey;
      });
      if (targetIndex === -1) {
        throw new Error('El producto ya no est? en el pedido.');
      }
      const nextItems = [...items];
      const currentItem = nextItems[targetIndex];
      const currentQuantity = Number(currentItem.quantity || 0);
      if (currentQuantity > 1) {
        nextItems[targetIndex] = {
          ...currentItem,
          quantity: currentQuantity - 1,
        };
      } else {
        nextItems.splice(targetIndex, 1);
      }
      const { subtotal, taxes, total } = calcTotals(nextItems);
      tx.update(ref, {
        items: nextItems,
        subtotal,
        taxes,
        total,
        updatedAt: serverTimestamp(),
      });
    });
  } catch (e) {
    console.error(e);
    alert(e?.message || 'No se pudo eliminar el producto.');
  } finally {
    setRemovingItems(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }
};

const deleteOrder = async (order) => {
  if (!order || (order.items || []).length > 0) return;
  setDeletingOrders(prev => ({ ...prev, [order.id]: true }));
  try {
    await deleteDoc(doc(db, 'orders', order.id));
    setDeleteDialog(null);
  } catch (e) {
    console.error(e);
    alert(e?.message || 'No se pudo eliminar el pedido.');
  } finally {
    setDeletingOrders(prev => {
      const next = { ...prev };
      delete next[order.id];
      return next;
    });
  }
};

const openDeleteDialog = (order) => {
  setDeleteDialog(order);
};

// Turno/base del usuario actual (cajero)
// Caja general: permite cobrar si existe **cualquier** caja abierta
async function getAnyOpenTurn() {
  // 1) Óptimo con orderBy (puede requerir índice: estado + openedAt)
  try {
    const q1 = query(
      collection(db, 'cajas'),
      where('estado', '==', 'abierta'),
      orderBy('openedAt', 'desc'),
      limit(1)
    );
    const s1 = await getDocs(q1);
    if (!s1.empty) {
      const d = s1.docs[0];
      return { id: d.id, ...d.data() };
    }
  } catch (e) {
    console.warn('getAnyOpenTurn: falta índice (estado, openedAt). Fallback…', e);
  }

  // 2) Fallback sin orderBy
  try {
    const q2 = query(
      collection(db, 'cajas'),
      where('estado', '==', 'abierta'),
      limit(1)
    );
    const s2 = await getDocs(q2);
    if (!s2.empty) {
      const d = s2.docs[0];
      return { id: d.id, ...d.data() };
    }
  } catch (e2) {
    console.error('getAnyOpenTurn (fallback) falló:', e2);
  }

  return null;
}

async function ensureTurn(){
 const t = await getOpenTurnForUser(currentUser);
   if (!t) throw new Error('No hay ninguna caja abierta.');
  return t;
}


  // helper: fecha creada robusta y "hace X"
  const toDate = (o) =>
    (o?.createdAt?.toDate?.() ? o.createdAt.toDate()
      : o?.orderTime ? new Date(o.orderTime)
      : null);

  const elapsed = (date) => {
    if (!date) return '—';
    const diff = clockTick - date.getTime();
    if (diff < 60_000) return 'justo ahora';
    const mins = Math.floor(diff / 60_000);
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60), m = mins % 60;
    return `${h}h ${m}m`;
  };

  // Suscripción en tiempo real a pedidos "En Preparacion"
  useEffect(() => {
const q = query(
   collection(db, 'orders'),
   where('status', 'in', ['En Preparacion','Listo para Cobro'])
);
const unsub = onSnapshot(q, (snap) => {
        setSnapshotError('');
        const list = snap.docs.map((d) => {
        const data = d.data();
        const created = toDate(data);
        return { id: d.id, ...data, _createdAt: created };
      });
      // fallback: si algún doc no tiene createdAt, ordena por _createdAt y luego por id
      list.sort((a, b) => {
        const at = a._createdAt?.getTime?.() ?? 0;
        const bt = b._createdAt?.getTime?.() ?? 0;
        return at === bt ? String(a.id).localeCompare(String(b.id)) : at - bt;
      });
setRawOrders(list);
    }, (err) => {
      console.error('Kitchen snapshot error:', err);
      setSnapshotError('Se perdió la conexión con los pedidos. Conservamos la última información visible mientras reconectamos.');
    });

    return () => unsub();
  }, []);

// El despacho depende del estado del pedido, no del estado de una caja.
// Una caja cerrada nunca debe ocultar un pedido que aún está activo.
useEffect(() => {
  setPendingOrders(rawOrders);
}, [rawOrders]);

const dispatchCounters = useMemo(() => pendingOrders.reduce((counts, order) => {
  const stage = getDispatchStage(order);
  if (stage === DISPATCH_STAGE.NEW) counts.new += 1;
  if (stage === DISPATCH_STAGE.PREPARING) counts.preparing += 1;
  if (stage === DISPATCH_STAGE.DELIVERED) counts.delivered += 1;
  return counts;
}, { new: 0, preparing: 0, delivered: 0 }), [pendingOrders]);

useEffect(() => {
  const intervalId = window.setInterval(() => setClockTick(Date.now()), 30_000);
  return () => window.clearInterval(intervalId);
}, []);

useEffect(() => {
  if (!soundEnabled || dispatchCounters.new === 0) return undefined;
  let active = true;
  const trigger = () => {
    playDispatchAlert().catch((error) => {
      if (!active) return;
      console.error('La alerta sonora fue bloqueada:', error);
      setSoundEnabled(false);
    });
  };
  if (skipNextAlarmRef.current) {
    skipNextAlarmRef.current = false;
  } else {
    trigger();
  }
  const intervalId = window.setInterval(trigger, 20_000);
  return () => {
    active = false;
    window.clearInterval(intervalId);
  };
}, [dispatchCounters.new, playDispatchAlert, soundEnabled]);

useEffect(() => {
  if (typeof document === 'undefined') return undefined;
  const originalTitle = document.title;
  if (dispatchCounters.new > 0) {
    document.title = `🔔 ${dispatchCounters.new} nuevo${dispatchCounters.new === 1 ? '' : 's'} | Despacho`;
  }
  return () => { document.title = originalTitle; };
}, [dispatchCounters.new]);


useEffect(() => {
  if (!currentUser) {
    setHasOpenTurn(false);
    setCheckingTurn(false);
    return undefined;
  }

  setCheckingTurn(true);
  let alive = true;
  let refreshVersion = 0;
  const refreshTurn = async () => {
    const version = ++refreshVersion;
    const mine = canChargeDispatchOrder(currentUser) ? await getOpenTurnForUser(currentUser) : null;
    const turn = mine || (canUseSharedDispatchCash(currentUser) ? await getAnyOpenTurn() : null);
    if (alive && version === refreshVersion) {
      setHasOpenTurn(Boolean(turn));
      setCheckingTurn(false);
    }
  };
  const unsubscribe = onSnapshot(query(collection(db, 'cajas'), where('estado', '==', 'abierta')),
    () => { void refreshTurn(); },
    (error) => {
      refreshVersion += 1;
      console.error('No se pudo validar la caja abierta en despacho:', error);
      if (alive) { setHasOpenTurn(false); setCheckingTurn(false); }
    });

  return () => {
    alive = false;
    if (unsubscribe) unsubscribe();
  };
}, [currentUser]);

const filteredOrders = useMemo(() => {
  const term = String(tableSearch || '').trim().toLowerCase();
  return pendingOrders
    .filter((order) => {
      if (statusFilter === 'Todos') return true;
      return getDispatchStage(order) === statusFilter;
    })
    .filter((order) => !term || String(order?.table || '').toLowerCase().includes(term))
    .sort((left, right) => {
      const stageDiff = dispatchStagePriority(left) - dispatchStagePriority(right);
      if (stageDiff !== 0) return stageDiff;
      const leftTime = left._createdAt?.getTime?.() ?? 0;
      const rightTime = right._createdAt?.getTime?.() ?? 0;
      return leftTime - rightTime;
    });
}, [pendingOrders, statusFilter, tableSearch]);

useEffect(() => {
  return () => {
    if (__printFrame) {
      __printFrame.remove?.();
      __printFrame = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close?.();
      audioContextRef.current = null;
    }
  };
}, []);


const openChargeHere = (order) => {
 if (!canChargeDispatchOrder(currentUser)) {
   alert('Tu usuario no puede cobrar este pedido desde despacho.');
   return;
 }
 if (!hasOpenTurn){ alert('No tienes base asignada. Solicítala al administrador.'); return; }
 setOrderToCharge(order);
  setShowCharge(true);
};

const printSaleTicketFallback = ({ invoiceNumber, order, payment }) => {
  const totals = calcTotals(order?.items || []);
  const content = `
${settings?.ticketDisplayName || settings?.storeName || 'ValenPOS'}
----------------------------------------
VENTA ${invoiceNumber ? `#${invoiceNumber}` : ''}
Mesa/Pedido: ${order?.table || '-'}
Mesero: ${order?.waiterName || '-'}
----------------------------------------
Items:
${(order?.items || []).map((i) => `${i.quantity}x ${i.name}  ${formatMoney(Number(i.price || 0) * Number(i.quantity || 0))}`).join('\n') || '-'}
----------------------------------------
Subtotal: ${formatMoney(totals.subtotal)}
${settings?.taxEnabled ? `Impuesto (${settings?.taxRate || 0}%): ${formatMoney(totals.taxes)}` : 'Impuesto: Exento'}
Total: ${formatMoney(totals.total)}
Pago 1: ${payment.paymentMethod}  ${formatMoney(payment.amount1 || 0)}
${payment.paymentMethod2 ? `Pago 2: ${payment.paymentMethod2}  ${formatMoney(payment.amount2 || 0)}` : ''}
Cambio: ${formatMoney(Math.max(0, (payment.amount1 || 0) + (payment.amount2 || 0) - totals.total))}
----------------------------------------
Gracias
----------------------------------------`;

  printWithIframe(`<pre class="ticket">${content}</pre>`);
};

const printSaleTicket = async ({ invoiceNumber, order, payment }) => {
  const totals = calcTotals(order?.items || []);
  return printTicket({
    storeName: settings?.ticketDisplayName || settings?.storeName || 'ValenPOS',
    address: settings?.address || '',
    phone: settings?.phone || '',
    email: settings?.email || '',
    invoiceNumber,
    date: new Date().toISOString(),
    customerName: order?.customerName || 'Consumidor Final',
    table: order?.table || '',
    waiterName: order?.waiterName || '',
    items: (order?.items || []).map((item) => ({
      name: item.name,
      quantity: Number(item.quantity || 0),
      price: Number(item.price || 0),
      total: Number(item.price || 0) * Number(item.quantity || 0),
    })),
    subtotal: Number(totals.subtotal || 0),
    taxEnabled: Boolean(settings?.taxEnabled),
    tax: Number(totals.taxes || 0),
    taxLabel: settings?.taxEnabled ? `Impuesto (${settings?.taxRate || 0}%)` : 'Impuesto',
    total: Number(totals.total || 0),
    paymentMethod: payment?.paymentMethod || '',
    amountReceived: Number(payment?.amount1 || 0),
    secondPaymentMethod: payment?.paymentMethod2 || '',
    secondAmountReceived: Number(payment?.amount2 || 0),
    change: Math.max(0, Number(payment?.amount1 || 0) + Number(payment?.amount2 || 0) - Number(totals.total || 0)),
    footer: 'Gracias',
  }, printingSettings, {
    browserPrint: () => printSaleTicketFallback({ invoiceNumber, order, payment }),
  });
};

const confirmChargeFromKitchen = async ({ paymentMethod, amount1, paymentMethod2, amount2 }) => {
  const activeOrderId = orderToCharge?.id;
  let persistedInvoice = null;
  try {
 setCharging(c => ({ ...c, [activeOrderId]: true }));
    if (!canChargeDispatchOrder(currentUser)) throw new Error('No tienes permiso para cobrar pedidos.');
    const turn = await getOpenTurnForUser(currentUser) ||
      (canUseSharedDispatchCash(currentUser) ? await getAnyOpenTurn() : null);
    if (!turn) throw new Error('No tienes una caja abierta autorizada. Solicítala al administrador.');
    const order = orderToCharge;
    if (order.status !== 'Listo para Cobro') {
  console.warn('Cobraste un pedido aún en preparación.');
}

    // Repara pedidos antiguos de recetas fijas guardados sin desglose. En
    // recetas con opciones no se adivina la selección original.
    const saleItems = (order.items || []).map((item) => {
      if (Array.isArray(item.recipeComponents) && item.recipeComponents.length > 0) return item;
      const catalogProduct = (products || []).find((product) => product.id === item.id);
      if (!catalogProduct?.config?.recipe) return item;
      const choiceGroups = safeList(catalogProduct.config.recipe.choiceGroups);
      if (choiceGroups.length > 0) {
        throw new Error(`El pedido no conserva los insumos elegidos para "${item.name}". Elimínalo y anéxalo nuevamente.`);
      }
      const components = getRecipeComponentsFromProduct(catalogProduct, products);
      if (components.length === 0) {
        throw new Error(`La receta de "${item.name}" no tiene insumos válidos configurados.`);
      }
      return { ...item, recipeComponents: components };
    });

    // Venta

    const { invoiceNumber, idempotentReplay } = await processOrderSale({
     order: {
       id: order.id,
      table: order.table,
      items: saleItems,
       waiterUid: order.waiterUid,
       waiterName: order.waiterName,
     },
     cashier: currentUser,
     paymentMethod, amount1, paymentMethod2, amount2,
     turnId: turn.id,
     items: saleItems,
   });

   persistedInvoice = invoiceNumber;

   // Actualizar pedido a Cobrado
   await updateDoc(doc(db,'orders',order.id), {
     status: 'Cobrado',
     paidAt: serverTimestamp(),
     saleInvoice: invoiceNumber,
     cashierUid: currentUser?.uid || null,
    cashierName: currentUser?.username || currentUser?.email || 'Cajero',
     turnId: turn.id,
   });

   if (printingSettings.autoPrintSales && !idempotentReplay) {
     try {
       await printSaleTicket({
         invoiceNumber,
         order,
         payment: { paymentMethod, amount1, paymentMethod2, amount2 },
       });
     } catch (printError) {
       console.error('Fallo la impresion de venta desde despacho:', printError);
       alert('El cobro se completo, pero la impresion fallo.');
     }
   }

   setShowCharge(false);
    setOrderToCharge(null);
 } catch (e) {
   console.error(e);
    alert(persistedInvoice
      ? `La venta ${persistedInvoice} ya se registró, pero no se pudo confirmar el pedido. No solicites otro pago; concilia esta factura. Detalle: ${e?.message || e}`
      : (e?.message || 'No se pudo completar el cobro.'));
  } finally {
    if (activeOrderId) {
      setCharging((current) => ({ ...current, [activeOrderId]: false }));
    }
  }
};
const printKitchenTicketFallback = (order) => {
  const waiter = order.waiterName || order.waiter?.username || order.waiter?.email || 'Mesero';
  const when = toDate(order) ?? new Date();
  const ticketContent = `
----------------------------------------
DESPACHO - PEDIDO #${order.id}
----------------------------------------
Mesa/Pedido: ${order.table}
Mesero: ${waiter}
Hora Pedido: ${when.toLocaleTimeString()}
----------------------------------------
Items:
${(order.items || []).map(it => `${it.quantity}x ${it.name}`).join('\n')}
----------------------------------------
Notas: ${order.notes || 'Ninguna'}
----------------------------------------
¡A DESPACHO!
----------------------------------------`;

  const html = `<pre class="ticket">${ticketContent}</pre>`;
  printWithIframe(html);
};

const printKitchenTicket = async (order) => {
  return printKitchen({
    table: order.table || '',
    waiterName: order.waiterName || order.waiter?.username || order.waiter?.email || 'Mesero',
    orderTime: (toDate(order) ?? new Date()).toISOString(),
    items: (order.items || []).map((item) => ({
      name: item.name,
      quantity: Number(item.quantity || 0),
      observation: item.observation || '',
    })),
    notes: order.notes || '',
    footer: 'A despacho',
  }, printingSettings, {
    browserPrint: () => printKitchenTicketFallback(order),
  });
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-orange-50 p-3 sm:p-6">
      {changingTableOrder && <ChangeTableModal
        order={changingTableOrder} orders={rawOrders} currentUser={currentUser}
        onClose={() => setChangingTableOrder(null)} onPrint={printKitchenTicket}
      />}
      <div className="mb-6 rounded-[28px] border border-slate-200 bg-white/95 p-4 shadow-sm sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <button
          onClick={onBack}
          className="flex items-center justify-center gap-2 rounded-2xl border border-sky-200 px-4 py-3 text-sm font-semibold text-sky-700 transition hover:border-sky-400 hover:text-sky-900 sm:w-fit"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Volver al Menú
        </button>
        <div className="flex-1 text-center lg:text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-orange-600">Cocina</p>
          <h1 className="mt-1 text-2xl font-black text-slate-900 sm:text-4xl">Panel de Despacho</h1>
          <p className="mt-2 text-sm text-slate-500">Nuevo, en preparación y entregado son estados distintos. Entregado no significa cobrado.</p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col">
          <button
            type="button"
            onClick={soundEnabled ? disableSound : enableSound}
            className={`rounded-2xl px-4 py-3 text-sm font-black transition ${
              soundEnabled
                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200'
                : 'bg-rose-600 text-white hover:bg-rose-700'
            }`}
          >
            {soundEnabled ? '🔊 Alertas activas' : '🔕 Activar sonido'}
          </button>
          {soundEnabled && (
            <button
              type="button"
              onClick={() => playDispatchAlert().catch(() => setSoundEnabled(false))}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-50"
            >
              Probar sonido
            </button>
          )}
        </div>
        </div>

        {snapshotError && (
          <div role="alert" className="mt-4 rounded-2xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-900">
            {snapshotError}
          </div>
        )}

        {dispatchCounters.new > 0 && (
          <div aria-live="assertive" className="mt-4 flex flex-col gap-3 rounded-2xl border-2 border-rose-400 bg-rose-50 px-4 py-4 text-rose-950 shadow-sm sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-lg font-black">🔔 {dispatchCounters.new} pedido{dispatchCounters.new === 1 ? '' : 's'} nuevo{dispatchCounters.new === 1 ? '' : 's'}</p>
              <p className="text-sm">La alarma se repetirá hasta que se confirme “Pedido visto”.</p>
            </div>
            {!soundEnabled && (
              <button type="button" onClick={enableSound} className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-black text-white">
                Activar alarma ahora
              </button>
            )}
          </div>
        )}

        <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { value: 'Todos', label: 'Activos', count: pendingOrders.length, tone: 'bg-slate-900 text-white' },
            { value: DISPATCH_STAGE.NEW, label: 'Nuevos', count: dispatchCounters.new, tone: 'bg-rose-100 text-rose-900' },
            { value: DISPATCH_STAGE.PREPARING, label: 'Preparando', count: dispatchCounters.preparing, tone: 'bg-orange-100 text-orange-900' },
            { value: DISPATCH_STAGE.DELIVERED, label: 'Entregados', count: dispatchCounters.delivered, tone: 'bg-indigo-100 text-indigo-900' },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setStatusFilter(item.value)}
              className={`rounded-2xl px-4 py-3 text-left transition ${item.tone} ${statusFilter === item.value ? 'ring-4 ring-sky-200' : 'opacity-80 hover:opacity-100'}`}
            >
              <span className="block text-xs font-bold uppercase tracking-[0.18em] opacity-70">{item.label}</span>
              <span className="mt-1 block text-2xl font-black">{item.count}</span>
            </button>
          ))}
        </div>

        <div className="mt-4">
          <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Buscar por mesa o nombre</label>
          <input
            type="text"
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
            placeholder="Ej: Mesa 8 o domicilio Ana"
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
          />
        </div>
      </div>

  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {filteredOrders.length > 0 ? (
          filteredOrders.map(order => {
            const created = order._createdAt;
            const totals = calcTotals(order.items || []);
            const stage = getDispatchStage(order);
            const canManage = canManageDispatchOrder(currentUser, order);
            const canDelete = canDeleteDispatchOrder(currentUser, order);
            const canEditContents = canEditDispatchContents(currentUser, order) && stage !== DISPATCH_STAGE.DELIVERED;
            const paymentPending = isPaymentPending(order);
            const ageMinutes = created ? Math.max(0, Math.floor((clockTick - created.getTime()) / 60_000)) : 0;
            const isLate = ageMinutes >= 15 && stage !== DISPATCH_STAGE.DELIVERED;
            const cardTone = stage === DISPATCH_STAGE.NEW
              ? 'border-rose-400 bg-rose-50/80 shadow-rose-100 ring-4 ring-rose-100'
              : stage === DISPATCH_STAGE.DELIVERED
                ? 'border-indigo-300 bg-indigo-50/70 shadow-indigo-100'
                : isLate
                  ? 'border-red-400 bg-red-50/60 shadow-red-100'
                  : 'border-orange-200 bg-white shadow-orange-100';
            const stageTone = stage === DISPATCH_STAGE.NEW
              ? 'bg-rose-600 text-white animate-pulse'
              : stage === DISPATCH_STAGE.DELIVERED
                ? 'bg-indigo-600 text-white'
                : 'bg-orange-100 text-orange-900';
            return (
              <div key={order.id} className={`flex flex-col rounded-[28px] border-2 p-4 shadow-lg sm:p-6 ${cardTone}`}>
<div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
  <div>
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Pedido</p>
    <h3 className="mt-1 text-xl font-black text-slate-900 sm:text-2xl">#{formatOrderId(order.id)}</h3>
  </div>
  <div className="flex flex-wrap gap-2 sm:justify-end">
    <span className={`inline-flex w-fit rounded-full px-3 py-1 text-sm font-black ${stageTone}`}>{stage}</span>
    {paymentPending && (
      <span className="inline-flex w-fit rounded-full bg-amber-200 px-3 py-1 text-sm font-black text-amber-950">Pago pendiente</span>
    )}
  </div>
</div>

                <p className="mb-1 text-sm font-bold uppercase tracking-[0.18em] text-slate-500">Mesa / pedido</p>
                <p className="mb-2 break-words text-3xl font-black text-slate-950 sm:text-4xl">
                  {order.table || 'Sin identificar'}
                </p>
                {canChangeOrderTable(currentUser, order) && (
                  <button type="button" disabled={Boolean(busy[order.id] || charging[order.id])}
                    onClick={() => setChangingTableOrder(order)}
                    className="mb-3 w-fit rounded-xl border border-sky-200 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-800 hover:bg-sky-100 disabled:opacity-50">
                    Cambiar mesa
                  </button>
                )}
                {order.lastTableChange && (
                  <p className="mb-3 rounded-xl bg-sky-50 px-3 py-2 text-sm text-sky-900">
                    Antes: {order.lastTableChange.from || 'Sin identificar'} · Cambiado por {order.lastTableChange.byName}
                    {order.lastTableChange.at?.toDate && ` · ${order.lastTableChange.at.toDate().toLocaleString('es-CO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}`}
                  </p>
                )}
                <div className={`mb-4 rounded-2xl px-3 py-2 text-sm ${isLate ? 'bg-red-600 font-black text-white' : 'bg-white/80 text-slate-600'}`}>
                  <span className="font-bold">Mesero:</span> {order.waiterName || order.waiter || '—'}
                  {' · '}<span className="font-bold">Hora:</span> {created ? created.toLocaleTimeString() : '—'}
                  {' · '}<span className="font-black">Hace {elapsed(created)}</span>
                </div>

                <div className="border-t border-gray-200 pt-4 mb-4 flex-1">
                  <h4 className="mb-2 font-black text-slate-800">Productos</h4>
                  <ul className="space-y-2 text-gray-800">
                    {(order.items || []).map((item, itemIndex) => {
                      const key = `${order.id}-${item.id}-${itemIndex}`;
                      const uniqueKey = item._lineId || key;
                      const isRemoving = Boolean(removingItems[uniqueKey]);
                      const unitPrice = Number(item.price || 0);
                      const quantity = Number(item.quantity || 0);
                      const lineTotal = unitPrice * quantity;
                      return (
                        <li key={uniqueKey} className="rounded-2xl border border-slate-200 bg-white/90 p-3">
                          <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <p className="text-lg font-black text-slate-950"><span className="text-rose-600">{quantity}×</span> {item.name}</p>
                            {item.observation && (
                              <p className="mt-2 rounded-xl bg-amber-100 px-3 py-2 text-sm font-bold text-amber-950">Observación: {item.observation}</p>
                            )}
                            <div className="mt-2 grid grid-cols-1 gap-1 text-sm text-slate-600 sm:grid-cols-2">
                              <p><span className="font-semibold text-slate-700">Unitario:</span> {formatMoney(unitPrice)}</p>
                              <p><span className="font-semibold text-slate-700">Total:</span> {formatMoney(lineTotal)}</p>
                            </div>
                          </div>
                          {canEditContents && (
                            <button
                              type="button"
                              disabled={isRemoving}
                              onClick={() => removeItemFromOrder(order, item, uniqueKey)}
                              className={`shrink-0 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                                isRemoving
                                  ? 'text-gray-500 border-gray-200 bg-gray-100 cursor-not-allowed'
                                  : 'text-red-700 border-red-200 bg-red-50 hover:bg-red-100'
                              }`}
                            >
                              {isRemoving ? 'Eliminando...' : 'Eliminar'}
                            </button>
                          )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  {order.notes && (
                    <div className="mt-3 rounded-2xl bg-amber-50 p-3 text-sm text-amber-900">
                      <span className="font-semibold">Notas:</span> {order.notes}
                    </div>
                  )}
                </div>

<div className="mb-4 grid grid-cols-2 gap-3 rounded-2xl bg-slate-900 p-4 text-sm text-white">
  <div>
    <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Subtotal</p>
    <p className="mt-1 text-lg font-bold">{formatMoney(totals.subtotal)}</p>
  </div>
  <div>
    <p className="text-xs uppercase tracking-[0.18em] text-slate-300">Total pedido</p>
    <p className="mt-1 text-lg font-bold">{formatMoney(totals.total)}</p>
  </div>
</div>

<div className="mt-auto grid grid-cols-1 gap-3 sm:grid-cols-2">
  <button
   disabled={!canPrintDispatchOrder(currentUser)}
   onClick={() => printKitchenTicket(order).catch((error) => alert(error?.message || 'No se pudo reimprimir.'))}
    className="rounded-2xl bg-gray-200 px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-300 transition duration-200"
  >
    Reimprimir
  </button>
 {canEditContents && (
   <button
      onClick={() => openAddProduct(order)}
      className="rounded-2xl bg-slate-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
    >
      Anexar producto
    </button>
 )}

 {canManage && stage === DISPATCH_STAGE.NEW && (
   <button
     type="button"
     disabled={busy[order.id] === true}
     onClick={() => handleAcknowledgeOrder(order)}
     className="rounded-2xl bg-rose-600 px-4 py-4 text-base font-black text-white shadow-sm transition hover:bg-rose-700 disabled:cursor-wait disabled:opacity-60 sm:col-span-2"
   >
     {busy[order.id] ? 'Confirmando...' : 'Pedido visto · Iniciar preparación'}
   </button>
 )}

 {canManage && stage === DISPATCH_STAGE.PREPARING && (
   <button
     type="button"
     disabled={busy[order.id] === true}
     onClick={() => handleMarkDelivered(order)}
     className="rounded-2xl bg-indigo-600 px-4 py-4 text-base font-black text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60 sm:col-span-2"
   >
     {busy[order.id] ? 'Guardando...' : 'Marcar como entregado'}
   </button>
 )}

 {stage === DISPATCH_STAGE.DELIVERED && (
   <div className="rounded-2xl border border-indigo-300 bg-indigo-100 px-4 py-3 text-center text-sm font-black text-indigo-950 sm:col-span-2">
     Entregado · todavía pendiente de cobro
   </div>
 )}

 {canManage && stage === DISPATCH_STAGE.DELIVERED && (
   <button
     type="button"
     disabled={busy[order.id] === true}
     onClick={() => handleRestoreToPreparing(order)}
     className="rounded-2xl border-2 border-orange-300 bg-orange-50 px-4 py-3 text-sm font-black text-orange-900 transition hover:bg-orange-100 disabled:cursor-wait disabled:opacity-60 sm:col-span-2"
   >
     {busy[order.id] ? 'Restableciendo...' : 'Volver a En preparación'}
   </button>
 )}

 {canManage && (
   <button
    onClick={() => openChargeHere(order)}
    disabled={checkingTurn || !hasOpenTurn || busy[order.id] === true || charging[order.id] === true}
    className={`rounded-2xl px-4 py-3 text-sm font-semibold text-white transition sm:col-span-2 ${
      (checkingTurn || !hasOpenTurn || busy[order.id] || charging[order.id])
        ? 'cursor-not-allowed bg-emerald-300'
        : 'bg-emerald-600 hover:bg-emerald-700'}`}
    title={checkingTurn ? 'Verificando base abierta...' : (!hasOpenTurn ? 'No hay una caja disponible para cobrar.' : '')}
   >
    Cobrar pedido
   </button>
 )}

 {canManage && !checkingTurn && !hasOpenTurn && (
   <p className="mt-1 text-xs font-bold text-amber-800 sm:col-span-2">No hay una caja disponible para cobrar. El pedido puede seguir en despacho.</p>
 )}

 {!canManage && (
   <p className="rounded-xl bg-slate-100 px-3 py-2 text-center text-xs font-bold text-slate-600 sm:col-span-2">{canChangeOrderTable(currentUser, order) ? 'Puedes cambiar la mesa de este pedido.' : 'Vista de consulta: este usuario no opera el pedido.'}</p>
 )}

 </div>
                {(order.items || []).length === 0 && canDelete && (
                  <button
                    type="button"
                    disabled={Boolean(deletingOrders[order.id])}
                    onClick={() => openDeleteDialog(order)}
                    className={`mt-3 rounded-2xl px-4 py-3 text-sm font-semibold transition duration-200 text-white ${
                      deletingOrders[order.id]
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-red-600 hover:bg-red-700'
                    }`}
                  >
                    {deletingOrders[order.id] ? 'Eliminando...' : 'Eliminar pedido'}
                  </button>
                )}
            </div>
            );
          })
        ) : (
          <div className="lg:col-span-3 bg-white rounded-xl shadow-lg p-6 text-center">
            <p className="text-xl text-gray-500">No hay pedidos en preparación.</p>
          </div>
        )}
      </div>
      <ChargeOrderModal
  open={showCharge}
  order={orderToCharge ? {
    table: orderToCharge.table,
    items: orderToCharge.items,
    waiterUid: orderToCharge.waiterUid,
    waiterName: orderToCharge.waiterName
  } : null}
  totalOverride={orderToCharge ? calcTotals(orderToCharge.items).total : 0}
  currentUser={currentUser}
  onClose={() => { setShowCharge(false); setOrderToCharge(null); }}
  onConfirm={confirmChargeFromKitchen}
  loading={Boolean(orderToCharge?.id && charging[orderToCharge.id])}
/>
{addModal.open && (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-5">
      <h3 className="text-lg font-semibold mb-3">
        Anexar producto — Pedido #{formatOrderId(addModal.order?.id)}
      </h3>

      <div className="flex gap-2 mb-3">
        <input
          value={productSearch}
          onChange={(e)=>setProductSearch(e.target.value)}
          placeholder="Buscar por nombre o código…"
          className="flex-1 border rounded px-3 py-2"
        />
        <input
          type="number"
          min={1}
          value={qty}
          onChange={(e)=>setQty(e.target.value)}
          className="w-24 border rounded px-3 py-2"
        />
      </div>

      <div className="h-64 overflow-y-auto border rounded p-2">
{catalog
  .filter(p => {
    const t = productSearch.trim().toLowerCase();
    if (!t) return true;
    return String(p.name||'').toLowerCase().includes(t) ||
           String(p.barcode||'').toLowerCase().includes(t);
  })
  .slice(0,100)
  .map(p => {
    const cap = Number(p._maxUnits || 0);
    const allowNegativeStock = p._allowNegativeStock === true;
    const out = !allowNegativeStock && cap <= 0;
    const isSelected = selectedProd?.id === p.id;

    const tag = allowNegativeStock ? 'Receta · permite faltantes' : `Stock: ${cap}`;

    return (
      <button
        key={p.id}
onClick={() => {
  if (out) return;
  // Helado: abre modal para elegir sabores
   if (p?.config?.isIceCream) {
     setIceModal({ open:true, product:p });
    return;
  }
  const choiceGroups = Array.isArray(p?.config?.recipe?.choiceGroups)
    ? p.config.recipe.choiceGroups
    : [];
  if (choiceGroups.length > 0) {
    setRecipeChoiceModal({ open:true, product:p });
    return;
  }
  // Productos normales o recetas sin elección
  setSelectedProd(p);
 }}    

        disabled={out}
        title={out ? 'Sin stock suficiente' : (allowNegativeStock ? 'Sus insumos pueden quedar en negativo' : '')}
        className={
          `w-full text-left px-3 py-2 rounded mb-1 border ` +
          (out
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : (isSelected
                ? 'bg-blue-50 border-blue-300'
                : 'hover:bg-gray-50 border-transparent'))
        }
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">{p.name}</div>
            <div className="text-xs text-gray-500">
              Cód: {p.barcode || '—'} · Precio: {Number(p.price||0).toLocaleString('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0})}
            </div>
          </div>
          <div className={`text-xs px-2 py-0.5 rounded-full ${out ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
            {tag}
          </div>
        </div>
      </button>
    );
  })}
   
      </div>

      <div className="mt-4 flex gap-2 justify-end">
        <button className="px-3 py-2 rounded border" onClick={()=>{ setAddModal({open:false,order:null}); setSelectedProd(null); }}>
          Cancelar
        </button>
        <button
          disabled={!selectedProd}
          onClick={confirmAddProduct}
          className={`px-3 py-2 rounded text-white ${selectedProd ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-gray-300 cursor-not-allowed'}`}
        >
          Anexar
        </button>
      </div>
    </div>
  </div>
)}

{deleteDialog && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
      <h3 className="text-xl font-semibold text-gray-900">Eliminar pedido</h3>
      <p className="text-sm text-gray-600">
        El pedido <span className="font-semibold">{deleteDialog.id}</span> no tiene productos.
        ¿Deseas eliminarlo definitivamente?
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={() => setDeleteDialog(null)}
          className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
        >
          Cancelar
        </button>
        <button
          onClick={() => deleteOrder(deleteDialog)}
          className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition"
          disabled={Boolean(deletingOrders[deleteDialog.id])}
        >
          {deletingOrders[deleteDialog.id] ? 'Eliminando...' : 'Eliminar'}
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal de sabores para helados */}
 <IceCreamPOS
  open={iceModal.open}
  product={iceModal.product}
  products={allProducts}
  onClose={() => setIceModal({ open:false, product:null })}
  onAddToCart={async (item) => {
    // item = { id, name(“Vaso 2 sabores - Vainilla / Fresa”), price, quantity:1, recipeComponents:[…] }
    try {
      const order = addModal.order;
       if (!order) return;

      // capacidad máxima calculada antes
      const catalogProduct = catalog.find(x => x.id === item.id);
      const cap = Number(catalogProduct?._maxUnits || 0);
      const allowNegativeStock = catalogProduct?._allowNegativeStock === true;
      if (!allowNegativeStock && cap <= 0) throw new Error('No hay stock suficiente para este producto.');

     await runTransaction(db, async (tx) => {
        const ref = doc(db,'orders', order.id);
       const snap = await tx.get(ref);
         if (!snap.exists()) throw new Error('Pedido no existe');
        const data = snap.data();
       const items = Array.isArray(data.items) ? [...data.items] : [];

        // guarda receta en el item para que al cobrar se descuenten insumos
              items.push({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: 1,
         barcode: '',
         _lineId: makeLineId(),
         recipeComponents: item.recipeComponents || []
        });

        const { subtotal, taxes, total } = calcTotals(items);
        tx.update(ref, { items, subtotal, taxes, total, updatedAt: serverTimestamp() });
      });

      setIceModal({ open:false, product:null });
      setAddModal({ open:false, order:null });
      setSelectedProd(null); setQty(1);
     } catch (e) {
       alert(e?.message || 'No se pudo anexar el producto.');
    }
  }}
 />

 <RecipeChoicePOS
  open={recipeChoiceModal.open}
  product={recipeChoiceModal.product}
  products={allProducts}
  onClose={() => setRecipeChoiceModal({ open:false, product:null })}
  onAddToCart={async (item) => {
    try {
      const order = addModal.order;
      if (!order) return;
      const quantityToAdd = Math.max(1, Number.parseInt(qty, 10) || 1);
      await runTransaction(db, async (tx) => {
        const ref = doc(db, 'orders', order.id);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error('Pedido no existe');
        const data = snap.data();
        const items = Array.isArray(data.items) ? [...data.items] : [];
        items.push({
          id: item.id,
          name: item.name,
          price: Number(item.price || 0),
          costPrice: Number(item.costPrice || 0),
          quantity: quantityToAdd,
          barcode: item.barcode || '',
          _lineId: makeLineId(),
          recipeComponents: item.recipeComponents || [],
        });
        const { subtotal, taxes, total } = calcTotals(items);
        tx.update(ref, { items, subtotal, taxes, total, updatedAt: serverTimestamp() });
      });
      setRecipeChoiceModal({ open:false, product:null });
      setAddModal({ open:false, order:null });
      setSelectedProd(null);
      setQty(1);
    } catch (error) {
      alert(error?.message || 'No se pudo anexar el producto configurado.');
    }
  }}
 />

    </div>
  );
};

export default KitchenView;
